import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Briefcase, Check, Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useIsMsp } from '@/hooks/useIsMsp';
import { useConfirm } from '@/hooks/useConfirm';
import {
  useBulkDesignateCustomers,
  useBulkUndesignateCustomers,
  useMspCustomerCandidates,
  useMspCustomers,
  useUpdateCustomer,
} from '@/features/msp';
import { type CustomerOnboardingResponse, useMSPOnboarding } from '@/hooks/useMSPOnboarding';
import type { BulkCustomerResult, MspCustomer, MspCustomerCandidate } from '@/types/mspCustomer';
import { RelativeTime } from '@/components/RelativeTime';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ProvisioningTokenDialog, ProvisioningTokenDialogNotice } from '@/components/msp/ProvisioningTokenDialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

/** The value the selects use for "no tier assigned" — Radix forbids an empty string. */
const NONE = '__none__';


/**
 * A brief "Saved" beside the control that was just edited.
 *
 * Inline rather than a toast: these edits happen in a table, several in a row,
 * and the question being answered is "did *that* field save" — which is a
 * question about a place on screen. Failures do get a toast, because those need
 * to interrupt.
 */
function SavedTick({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-sev-4" role="status">
      <Check className="w-3 h-3" aria-hidden="true" />
      Saved
    </span>
  );
}

/** Reports which divisions a batch actually converted, and why the rest didn't. */
function BulkOutcome({ result, nameOf }: { result: BulkCustomerResult; nameOf: (id: string) => string }) {
  if (result.failed.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          {result.succeeded.length === 1
            ? '1 division is now a customer.'
            : `${String(result.succeeded.length)} divisions are now customers.`}
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="destructive">
      <AlertDescription>
        <p className="mb-1">
          {result.succeeded.length > 0
            ? `${String(result.succeeded.length)} converted, ${String(result.failed.length)} could not be:`
            : 'None could be converted:'}
        </p>
        <ul className="list-disc pl-5 space-y-0.5">
          {result.failed.map((failure) => (
            <li key={failure.divisionId}>
              <span className="font-medium">{nameOf(failure.divisionId)}</span> — {failure.reason}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

export default function MSPCustomers() {
  const { user } = useAuth();
  const organizationId = user?.organizationId;
  const isMsp = useIsMsp();
  const { confirm, confirmDialog } = useConfirm();

  const { data: customerList, isLoading, isError, error } = useMspCustomers(organizationId, isMsp, {
    includeMetrics: true,
  });
  const customers = useMemo(() => customerList?.customers ?? [], [customerList]);
  const vocabulary = customerList?.vocabulary ?? { lifecycle: [], tier: [] };

  const CANDIDATE_PAGE_SIZE = 50;
  const [candidateOffset, setCandidateOffset] = useState(0);
  const { data: candidateData } = useMspCustomerCandidates(organizationId, isMsp, {
    limit: CANDIDATE_PAGE_SIZE,
    offset: candidateOffset,
  });
  const candidates = candidateData?.candidates;
  const candidateTotal = candidateData?.total ?? 0;
  // Names of every candidate page the user has seen, so a partial bulk failure
  // can be reported by name even for a division on a page they have since left —
  // the selection spans pages but this page's `candidates` array does not.
  const candidateNames = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    for (const c of candidates ?? []) candidateNames.current.set(c.divisionId, c.name);
  }, [candidates]);
  const updateCustomer = useUpdateCustomer();
  const bulkDesignate = useBulkDesignateCustomers();
  const bulkUndesignate = useBulkUndesignateCustomers();
  const onboardingMutation = useMSPOnboarding();

  const [savedField, setSavedField] = useState<string | null>(null);
  const savedTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => { window.clearTimeout(savedTimer.current); }, []);

  /**
   * Applies one field change, and says what happened. A silent success leaves
   * the user checking the network tab; a silent failure is worse — it looks
   * exactly like a success.
   */
  const saveField = useCallback(
    (divisionId: string, field: 'lifecycle' | 'tier' | 'accountManager', value: string, label: string) => {
      const key = `${divisionId}:${field}`;
      updateCustomer.mutate(
        { divisionId, [field]: value },
        {
          onSuccess: () => {
            setSavedField(key);
            window.clearTimeout(savedTimer.current);
            savedTimer.current = window.setTimeout(() => {
              setSavedField((current) => (current === key ? null : current));
            }, 2500);
          },
          onError: (err) => {
            const detail = (err as { response?: { data?: { description?: string; error?: string } } }).response?.data;
            toast.error(`Could not update ${label}`, {
              description: detail?.description ?? detail?.error ?? err.message,
            });
          },
        },
      );
    },
    [updateCustomer],
  );

  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerSelection, setPickerSelection] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkResult, setBulkResult] = useState<BulkCustomerResult | null>(null);

  // Onboarding sheet state, unchanged from before this page was rewritten.
  const [isOnboardModalActive, setIsOnboardModalActive] = useState(false);
  const [isSuccessModalActive, setIsSuccessModalActive] = useState(false);
  const [onboardingResult, setOnboardingResult] = useState<CustomerOnboardingResponse | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [regionName, setRegionName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const openOnboardModal = () => {
    setCustomerName('');
    setRegionName('');
    setAdminEmail('');
    setAdminFirstName('');
    setAdminLastName('');
    setSubmitError(null);
    setIsOnboardModalActive(true);
  };

  const handleOnboard = (e: React.SubmitEvent) => {
    e.preventDefault();
    setSubmitError(null);
    onboardingMutation.mutate(
      {
        customerName,
        regionName: regionName || undefined,
        adminEmail: adminEmail || undefined,
        adminFirstName: adminFirstName || undefined,
        adminLastName: adminLastName || undefined,
      },
      {
        onSuccess: (data) => {
          setIsOnboardModalActive(false);
          setOnboardingResult(data);
          setIsSuccessModalActive(true);
        },
        onError: (err) => {
          setSubmitError(
            err.response?.data.description || err.response?.data.error || 'Failed to onboard customer.',
          );
        },
      },
    );
  };

  const nameOf = (divisionId: string) =>
    customers.find((c) => c.divisionId === divisionId)?.name ??
    candidateNames.current.get(divisionId) ??
    divisionId;

  const convertSelected = async () => {
    const divisionIds = [...pickerSelection];
    if (divisionIds.length === 0) return;
    const result = await bulkDesignate.mutateAsync({ divisionIds });
    setBulkResult(result);
    setPickerSelection(new Set());
    // Leave the sheet open on a partial failure so the reasons stay readable.
    if (result.failed.length === 0) setIsPickerOpen(false);
  };

  const removeSelected = () => {
    const divisionIds = [...selected];
    if (divisionIds.length === 0) return;
    void confirm({
      title:
        divisionIds.length === 1
          ? `Remove ${nameOf(divisionIds[0])} from customers?`
          : `Remove ${String(divisionIds.length)} customers?`,
      message:
        'This removes the customer designation only. Divisions, regions, devices, tags and commercial records are all kept, and they can be designated again at any time.',
      confirmText: 'Remove designation',
    }).then(async (ok) => {
      if (!ok) return;
      const result = await bulkUndesignate.mutateAsync({ divisionIds });
      setBulkResult(result);
      setSelected(new Set());
    });
  };

  if (!isMsp) {
    return (
      <PageContainer>
        <PageHeader title="Customers" />
        <EmptyState
          variant="no-data"
          title="MSP mode is off"
          description="Turn on MSP mode under Organization Admin → General to manage divisions as customers."
        />
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title="Customers" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <PageHeader title="Customers" />
        <Alert variant="destructive">
          <AlertDescription>Could not load customers: {error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const columns: readonly ResponsiveTableColumn<MspCustomer>[] = [
    {
      key: 'name',
      label: 'Customer',
      hideMobileLabel: true,
      // A link on the name rather than a clickable row: every other cell in this
      // table holds a Select or an Input, and a row handler would fire whenever
      // one of those was operated. A link also keeps keyboard access, middle-click
      // and "copy link address" working, which a click handler would not.
      render: (c) => (
        <Link to={`/customers/${c.divisionId}`} className="font-medium hover:underline">
          {c.name}
        </Link>
      ),
    },
    {
      key: 'lifecycle',
      label: 'Lifecycle',
      render: (c) => (
        <span className="inline-flex items-center gap-2">
          <Select
            value={c.lifecycle ?? NONE}
            onValueChange={(value) => { saveField(c.divisionId, 'lifecycle', value, 'lifecycle'); }}
          >
            <SelectTrigger className="h-8 w-36" aria-label={`Lifecycle for ${c.name}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {vocabulary.lifecycle.map((option) => (
                <SelectItem key={option.value} value={option.value} className="capitalize">
                  {option.displayName ?? option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SavedTick show={savedField === `${c.divisionId}:lifecycle`} />
        </span>
      ),
    },
    {
      key: 'tier',
      label: 'Tier',
      hideAt: 'mobile',
      render: (c) => (
        <span className="inline-flex items-center gap-2">
          <Select
            value={c.tier ?? NONE}
            onValueChange={(value) => { saveField(c.divisionId, 'tier', value === NONE ? '' : value, 'tier'); }}
          >
            <SelectTrigger className="h-8 w-32" aria-label={`Tier for ${c.name}`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>No tier</SelectItem>
              {vocabulary.tier.map((option) => (
                <SelectItem key={option.value} value={option.value} className="capitalize">
                  {option.displayName ?? option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SavedTick show={savedField === `${c.divisionId}:tier`} />
        </span>
      ),
    },
    {
      key: 'accountManager',
      label: 'Account manager',
      hideAt: 'mobile',
      render: (c) => (
        <span className="inline-flex items-center gap-2">
          <Input
            // Keyed on the saved value so a successful save re-seeds the field,
            // and a failed one is visibly reverted rather than left showing text
            // that was never stored.
            key={`${c.divisionId}:${c.accountManager ?? ''}`}
            defaultValue={c.accountManager ?? ''}
            placeholder="Unassigned"
            className="h-8 w-44"
            aria-label={`Account manager for ${c.name}`}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next !== (c.accountManager ?? '')) {
                saveField(c.divisionId, 'accountManager', next, 'account manager');
              }
            }}
          />
          <SavedTick show={savedField === `${c.divisionId}:accountManager`} />
        </span>
      ),
    },
    {
      key: 'devices',
      label: 'Devices',
      align: 'right',
      render: (c) => <span className="text-xs tabular-nums">{c.rollup?.deviceTotal ?? 0}</span>,
    },
    {
      key: 'gateways',
      label: 'Gateways',
      align: 'right',
      hideAt: 'mobile',
      render: (c) => (
        <span className="text-xs tabular-nums">
          {c.rollup ? `${String(c.rollup.gatewaysOnline)}/${String(c.rollup.gatewayCount)}` : '—'}
        </span>
      ),
    },
    {
      key: 'since',
      label: 'Customer since',
      hideAt: 'mobile',
      render: (c) => <RelativeTime value={c.designatedAt} className="text-xs text-muted-foreground" />,
    },
  ];

  const candidateColumns: readonly ResponsiveTableColumn<MspCustomerCandidate>[] = [
    {
      key: 'name',
      label: 'Division',
      hideMobileLabel: true,
      render: (d) => (
        <span className="inline-flex items-center gap-2">
          <span className="font-medium">{d.name}</span>
          {d.likelyCustomer ? (
            <Badge className="border-0 font-normal gap-1 bg-sev-5/15 text-sev-5">
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              Likely customer
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'devices',
      label: 'Devices',
      align: 'right',
      render: (d) => <span className="text-xs tabular-nums">{d.deviceCount}</span>,
    },
    {
      key: 'gateways',
      label: 'Gateways',
      align: 'right',
      render: (d) => <span className="text-xs tabular-nums">{d.gatewayCount}</span>,
    },
    {
      key: 'regions',
      label: 'Regions',
      align: 'right',
      hideAt: 'mobile',
      render: (d) => <span className="text-xs tabular-nums">{d.regionCount}</span>,
    },
  ];

  const openPicker = () => {
    setPickerSelection(new Set());
    setBulkResult(null);
    setCandidateOffset(0);
    setIsPickerOpen(true);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Customers"
        subtitle={customers.length === 1 ? '1 customer' : `${String(customers.length)} customers`}
        primary={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openPicker} className="gap-2">
              <Briefcase className="w-4 h-4" aria-hidden="true" />
              Add existing division
            </Button>
            <Button onClick={openOnboardModal} className="gap-2">
              <Plus className="w-4 h-4" aria-hidden="true" />
              Onboard new customer
            </Button>
          </div>
        }
      />

      {bulkResult ? (
        <div className="mb-4">
          <BulkOutcome result={bulkResult} nameOf={nameOf} />
        </div>
      ) : null}

      {customers.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No customers yet"
          description={
            candidateTotal > 0
              ? 'Convert an existing division, or onboard a brand-new customer.'
              : 'Onboard your first customer to get started.'
          }
          action={
            candidateTotal > 0 ? (
              <Button onClick={openPicker}>Add existing division</Button>
            ) : (
              <Button onClick={openOnboardModal}>Onboard new customer</Button>
            )
          }
        />
      ) : (
        <>
          {selected.size > 0 ? (
            <div className="flex items-center justify-between gap-2 mb-3 rounded-md border border-border bg-bg-subtle px-3 py-2">
              <span className="text-sm">
                {selected.size === 1 ? '1 customer selected' : `${String(selected.size)} customers selected`}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setSelected(new Set()); }}>
                  Clear
                </Button>
                <Button variant="outline" size="sm" onClick={removeSelected} disabled={bulkUndesignate.isPending}>
                  Remove from customers
                </Button>
              </div>
            </div>
          ) : null}

          <ResponsiveTable<MspCustomer>
            columns={columns}
            rows={customers}
            rowKey={(c) => c.divisionId}
            selectable
            selectedKeys={selected}
            onSelectionChange={setSelected}
          />
        </>
      )}

      <AppSheet
        open={isPickerOpen}
        onOpenChange={(open) => { if (!open) setIsPickerOpen(false); }}
        title="Add existing divisions as customers"
        size="lg"
      >
        <AppSheet.Body>
          <p className="text-sm text-muted-foreground mb-3">
            Divisions that are not customers yet. Ones that look like they came from the customer-onboarding flow are
            marked — that is a hint to check, not a decision already made.
          </p>
          {bulkResult && bulkResult.failed.length > 0 ? (
            <div className="mb-3">
              <BulkOutcome result={bulkResult} nameOf={nameOf} />
            </div>
          ) : null}
          <ResponsiveTable<MspCustomerCandidate>
            columns={candidateColumns}
            rows={candidates ?? []}
            rowKey={(d) => d.divisionId}
            selectable
            selectedKeys={pickerSelection}
            onSelectionChange={setPickerSelection}
            empty={<EmptyState variant="no-data" title="Every division is already a customer" />}
          />
          {candidateTotal > CANDIDATE_PAGE_SIZE ? (
            <div className="flex items-center justify-between gap-2 mt-3 text-sm">
              <span className="text-muted-foreground">
                {/* Selection spans pages, so say how many are chosen across the whole set. */}
                Showing {candidateOffset + 1}–{Math.min(candidateOffset + CANDIDATE_PAGE_SIZE, candidateTotal)} of{' '}
                {candidateTotal}
                {pickerSelection.size > 0 ? ` · ${String(pickerSelection.size)} selected` : ''}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={candidateOffset === 0}
                  onClick={() => { setCandidateOffset((o) => Math.max(0, o - CANDIDATE_PAGE_SIZE)); }}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={candidateOffset + CANDIDATE_PAGE_SIZE >= candidateTotal}
                  onClick={() => { setCandidateOffset((o) => o + CANDIDATE_PAGE_SIZE); }}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button variant="outline" onClick={() => { setIsPickerOpen(false); }}>
            Cancel
          </Button>
          <Button
            onClick={() => { void convertSelected(); }}
            disabled={pickerSelection.size === 0 || bulkDesignate.isPending}
          >
            {bulkDesignate.isPending
              ? 'Converting…'
              : pickerSelection.size === 0
                ? 'Convert'
                : `Convert ${String(pickerSelection.size)}`}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <AppSheet
        open={isOnboardModalActive}
        onOpenChange={(open) => { if (!open) setIsOnboardModalActive(false); }}
        title="Onboard new customer"
        size="md"
      >
        <AppSheet.Body>
          <form id="onboard-form" onSubmit={handleOnboard} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ob-name">Customer name</Label>
              <Input
                id="ob-name"
                type="text"
                placeholder="Acme Corp"
                value={customerName}
                onChange={(e) => { setCustomerName(e.target.value); }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ob-region">Region name (optional)</Label>
              <Input
                id="ob-region"
                type="text"
                placeholder={
                  customerName ? `${customerName} Region` : 'Customer Region'
                }
                value={regionName}
                onChange={(e) => { setRegionName(e.target.value); }}
              />
              <p className="text-xs text-muted-foreground">
                Defaults to &quot;{customerName || 'Customer'} Region&quot; if left blank.
              </p>
            </div>

            <Separator />
            <h3 className="text-sm font-semibold">Admin invitation (optional)</h3>

            <div className="space-y-1.5">
              <Label htmlFor="ob-email">Admin email</Label>
              <Input
                id="ob-email"
                type="email"
                placeholder="admin@customer.com"
                value={adminEmail}
                onChange={(e) => { setAdminEmail(e.target.value); }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ob-first">First name</Label>
                <Input
                  id="ob-first"
                  type="text"
                  placeholder="First name"
                  value={adminFirstName}
                  onChange={(e) => { setAdminFirstName(e.target.value); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ob-last">Last name</Label>
                <Input
                  id="ob-last"
                  type="text"
                  placeholder="Last name"
                  value={adminLastName}
                  onChange={(e) => { setAdminLastName(e.target.value); }}
                />
              </div>
            </div>

            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setIsOnboardModalActive(false); }}
            disabled={onboardingMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="onboard-form"
            disabled={onboardingMutation.isPending}
          >
            {onboardingMutation.isPending ? 'Saving…' : 'Onboard customer'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {onboardingResult ? (
        <ProvisioningTokenDialog
          open={isSuccessModalActive}
          onOpenChange={(open) => { if (!open) setIsSuccessModalActive(false); }}
          title="Customer onboarded successfully"
          token={onboardingResult.provisioningToken.token}
          tokenName={onboardingResult.provisioningToken.name}
        >
          <ProvisioningTokenDialogNotice>
            <strong>{onboardingResult.division.name}</strong> has been successfully onboarded.
          </ProvisioningTokenDialogNotice>

          <div>
            <h4 className="text-sm font-semibold mb-2">Created resources</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Division:</strong> {onboardingResult.division.name} ({onboardingResult.division.slug})
              </li>
              <li>
                <strong>Region:</strong> {onboardingResult.region.name}
              </li>
              <li>
                <strong>Context:</strong> {onboardingResult.context.name}
              </li>
              <li>
                <strong>Team:</strong> {onboardingResult.team.name}
              </li>
            </ul>
          </div>

          {onboardingResult.invitation ? (
            <div>
              <h4 className="text-sm font-semibold mb-2">Admin invitation</h4>
              <p>
                An invitation has been sent to <strong>{onboardingResult.invitation.email}</strong>.
              </p>
              <p className="text-xs text-muted-foreground">
                Expires:{' '}
                <RelativeTime value={onboardingResult.invitation.expiresAt} absolute className="text-xs" />
              </p>
            </div>
          ) : null}
        </ProvisioningTokenDialog>
      ) : null}
      {confirmDialog}
    </PageContainer>
  );
}
